import React, { useState, useEffect, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import DataTable from '../../components/admin/DataTable';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import ErrorState from '../../components/common/ErrorState';
import { fetchInquiries, fetchUsers } from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { formatDateTime } from '../../utils/formatters';

// 답변 템플릿
const ANSWER_TEMPLATES = [
  { label: '접수 확인', text: '안녕하세요. 문의 주신 내용 확인하였습니다. 빠른 시일 내에 처리하여 안내드리겠습니다.' },
  { label: '배송 안내', text: '주문하신 상품은 정상 배송 진행 중입니다. 주문번호와 함께 문의 주시면 상세 배송 상황을 안내드리겠습니다.' },
  { label: '감사 인사', text: '문의해 주셔서 감사합니다. 추가로 궁금하신 점이 있으시면 언제든 다시 문의 주세요.' },
];

// 미답변 SLA 경고 기준(시간)
const SLA_HOURS = 24;

const InquiryManagePage = () => {
  const { showToast } = useToast();

  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [answer, setAnswer] = useState('');

  const loadInquiries = async () => {
    setLoading(true);
    setError(false);
    try {
      const [inquiriesData, usersData] = await Promise.all([fetchInquiries(), fetchUsers()]);
      const transformed = inquiriesData.map((inquiry) => {
        const user = usersData.find((u) => u.id === inquiry.userId);
        return { ...inquiry, userName: user ? user.name : '알 수 없음' };
      });
      setInquiries(transformed);
    } catch (e) {
      console.error('문의 데이터 로드 실패:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInquiries();
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(inquiries.map((i) => i.category).filter(Boolean))),
    [inquiries]
  );

  const isOverdue = (inquiry) => {
    if (inquiry.status !== 'pending' || !inquiry.createdAt) return false;
    const hours = (Date.now() - new Date(inquiry.createdAt).getTime()) / (1000 * 60 * 60);
    return hours > SLA_HOURS;
  };

  // 미답변 우선 정렬 + 검색/필터
  const filteredInquiries = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = inquiries.filter((inquiry) => {
      const matchStatus = filterStatus === 'all' || inquiry.status === filterStatus;
      const matchCat = categoryFilter === 'all' || inquiry.category === categoryFilter;
      const matchSearch =
        !term ||
        inquiry.title?.toLowerCase().includes(term) ||
        inquiry.content?.toLowerCase().includes(term) ||
        inquiry.userName?.toLowerCase().includes(term);
      return matchStatus && matchCat && matchSearch;
    });
    return [...filtered].sort((a, b) => {
      // 미답변(pending) 우선
      if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  }, [inquiries, filterStatus, categoryFilter, searchTerm]);

  const handleRowClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setAnswer(inquiry.answer || '');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInquiry(null);
    setAnswer('');
  };

  const handleSubmitAnswer = (e) => {
    e.preventDefault();
    if (!answer.trim()) {
      showToast('답변을 입력해주세요.', 'error');
      return;
    }
    setInquiries((prev) =>
      prev.map((inquiry) =>
        inquiry.id === selectedInquiry.id
          ? { ...inquiry, answer, answeredAt: new Date().toISOString(), status: 'answered' }
          : inquiry
      )
    );
    // 회원 알림 시연
    showToast('답변이 등록되었습니다. (회원에게 알림이 발송되었습니다)', 'success');
    handleCloseModal();
  };

  const getStatusBadge = (status, inquiry) => {
    const colors = { pending: 'bg-yellow-100 text-yellow-800', answered: 'bg-green-100 text-green-800' };
    const text = { pending: '답변대기', answered: '답변완료' };
    const overdue = inquiry && isOverdue(inquiry);
    return (
      <span className="inline-flex items-center gap-1">
        <span className={`rounded-full px-2 py-1 text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
          {text[status] || status}
        </span>
        {overdue && (
          <span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-600" title={`${SLA_HOURS}시간 초과`}>
            <AlertTriangle className="h-3.5 w-3.5" /> 지연
          </span>
        )}
      </span>
    );
  };

  const columns = [
    { key: 'id', label: 'ID', sortable: true, sortType: 'number' },
    { key: 'category', label: '카테고리', sortable: true },
    { key: 'title', label: '제목', sortable: true },
    { key: 'userName', label: '작성자', sortable: true },
    { key: 'createdAt', label: '작성일', sortable: true, sortType: 'date',
      render: (v) => (v ? new Date(v).toLocaleDateString('ko-KR') : '-') },
    {
      key: 'status', label: '상태', sortable: true,
      render: (status, row) => getStatusBadge(status, row),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">1:1 문의 관리</h1>
      </div>

      {error ? (
        <ErrorState onRetry={loadInquiries} />
      ) : (
        <>
          <div className="mb-4 space-y-3">
            <input
              type="text"
              placeholder="제목 / 내용 / 작성자로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500 md:w-96"
            />
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'all', label: '전체' },
                { key: 'pending', label: '답변대기' },
                { key: 'answered', label: '답변완료' },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilterStatus(f.key)}
                  className={`rounded-md px-4 py-2 ${
                    filterStatus === f.key ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="카테고리 필터"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">전체 카테고리</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredInquiries}
            onRowClick={handleRowClick}
            emptyMessage="조건에 맞는 문의가 없습니다."
            loading={loading}
            pageSize={10}
          />

          {!loading && (
            <div className="mt-4 text-sm text-gray-600">
              총 {filteredInquiries.length}개의 문의가 있습니다.
            </div>
          )}
        </>
      )}

      {/* 문의 상세 및 답변 모달 */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title="문의 상세" size="lg">
        {selectedInquiry && (
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 p-4">
              <div className="mb-3">
                <p className="mb-1 text-xs text-gray-500">카테고리</p>
                <p className="font-medium">{selectedInquiry.category}</p>
              </div>
              <div className="mb-3">
                <p className="mb-1 text-xs text-gray-500">제목</p>
                <p className="font-medium">{selectedInquiry.title}</p>
              </div>
              <div className="mb-3">
                <p className="mb-1 text-xs text-gray-500">문의 내용</p>
                <p className="whitespace-pre-wrap text-sm">{selectedInquiry.content}</p>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <p>작성자: {selectedInquiry.userName}</p>
                <p>작성일: {selectedInquiry.createdAt ? formatDateTime(selectedInquiry.createdAt) : '-'}</p>
              </div>
            </div>

            <form onSubmit={handleSubmitAnswer}>
              {/* 답변 템플릿 */}
              <div className="mb-2 flex flex-wrap gap-2">
                {ANSWER_TEMPLATES.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => setAnswer(t.text)}
                    className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <label className="mb-2 block text-sm font-medium text-gray-700">
                답변 {selectedInquiry.status === 'answered' && '(수정)'}
              </label>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={6}
                className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500"
                placeholder="답변 내용을 입력하세요"
                required
              />

              {selectedInquiry.answeredAt && (
                <p className="mt-2 text-xs text-gray-500">
                  답변일: {formatDateTime(selectedInquiry.answeredAt)}
                </p>
              )}

              <div className="mt-4 flex gap-3">
                <Button type="submit" variant="primary" fullWidth>
                  {selectedInquiry.status === 'answered' ? '답변 수정' : '답변 등록'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseModal} fullWidth>
                  닫기
                </Button>
              </div>
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InquiryManagePage;
